import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { createQr, pollQr } from '../api/auth'
import { ApiError } from '../api/client'
import type { SessionUser } from '../types/api'

interface Props {
  onSuccess: (user: SessionUser) => void
}

export function BilibiliLoginDialog({ onSuccess }: Props) {
  const [qr, setQr] = useState<{ qrcode_key: string; image_url?: string; url?: string }>()
  const [qrImage, setQrImage] = useState<string>()
  const [state, setState] = useState('点击下方按钮获取二维码')
  const [loading, setLoading] = useState(false)

  const start = async () => {
    setLoading(true)
    setState('正在创建安全登录请求…')
    try {
      const result = await createQr()
      setQr(result)
      setQrImage(result.image_url)
      if (!result.image_url && result.url) {
        setQrImage(await QRCode.toDataURL(result.url, {
          width: 220,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: '#111827', light: '#ffffff' },
        }))
      }
      setState('请使用 B 站 App 扫码确认')
    } catch (error) {
      setQr(undefined)
      setQrImage(undefined)
      setState(error instanceof ApiError ? error.message : '二维码获取失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!qr?.qrcode_key) return
    let cancelled = false
    const poll = async () => {
      try {
        const result = await pollQr(qr.qrcode_key)
        if (cancelled) return
        if (result.status === 'success' && result.user) onSuccess(result.user)
        else if (result.status === 'expired') setState('二维码已过期，请重新获取')
        else if (result.status === 'scanned') setState('已扫码，请在 B 站 App 确认')
      } catch (error) {
        if (!cancelled) setState(error instanceof ApiError ? error.message : '登录轮询失败')
      }
    }
    const timer = window.setInterval(poll, 2500)
    poll()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [qr?.qrcode_key, onSuccess])

  return (
    <section className="login-card" aria-label="Biliup B站扫码登录">
      <div className="login-mark">B</div>
      <span className="eyebrow">BILIUP</span>
      <h1>登录 Biliup</h1>
      <p>使用 B 站扫码登录，授权读取账号内容和数据。</p>
      {qr ? (
        <div className="qr-wrap">
          {qrImage ? <img src={qrImage} alt="B 站登录二维码" /> : <div className="qr-placeholder"><div>二维码生成中…</div><small>{qr.url}</small></div>}
          <strong>{state}</strong>
        </div>
      ) : (
        <>
          <div className="login-empty">
            <span className="login-empty-icon"><i className="ri-qr-scan-2-line" aria-hidden="true" /></span>
            <div><strong>使用 B 站 App 扫码</strong><small>登录后即可使用</small></div>
          </div>
          {state !== '点击下方按钮获取二维码' && <div className="inline-notice" role="alert">{state}</div>}
        </>
      )}
      <button className="btn btn-wide btn-primary" onClick={start} disabled={loading}>
        {loading ? '请稍候…' : qr ? '重新获取二维码' : '获取登录二维码'} <span>→</span>
      </button>
      <small className="login-note">登录凭据只在服务端加密保存，浏览器仅保留 HttpOnly 会话。</small>
    </section>
  )
}
